import type { DamageType } from '../config/constants';

export interface TowerStageDef {
  name: string;
  cost: number;
  damage: number;
  range: number;
  fireRate: number; // shots per second
  damageType: DamageType;
  splash?: number;
  burnDps?: number;
  slowPct?: number;
  armourPen?: number;
  critChance?: number;
  projectiles?: number;
  /** Guided missile projectile that re-acquires its target in flight. */
  homing?: boolean;
  /** Number of extra enemies an energy arc chains to. */
  chain?: number;
  specialName?: string;
  specialDesc?: string;
  specialCooldown?: number;
  specialDamageMul?: number;
  specialDuration?: number;
  specialRange?: number;
  visual: string;
}

export interface TowerEvolutionDef {
  to: string;
  name: string;
  desc: string;
  cost: number;
}

export interface TowerDef {
  id: string;
  name: string;
  short: string;
  desc: string;
  colour: number;
  stages: TowerStageDef[];
  targeting: 'ground' | 'air' | 'any';
  /** Evolution options offered once the tower reaches EXPERIMENTAL. */
  evolvesTo?: TowerEvolutionDef[];
  /** Symbiosis modules that can be bolted on once the tower reaches EXPERIMENTAL. */
  symbiosis?: string[];
}

export const TOWERS: Record<string, TowerDef> = {
  ballistic: {
    id: 'ballistic',
    name: 'Kestrel Autocannon',
    short: 'KESTREL',
    desc: 'Rapid ballistic fire. Excellent against light and medium targets.',
    colour: 0x6ec6ff,
    targeting: 'ground',
    stages: [
      {
        name: 'BASE',
        cost: 40,
        damage: 8,
        range: 110,
        fireRate: 4.5,
        damageType: 'BALLISTIC',
        visual: 'mk1',
      },
      {
        name: 'MK II',
        cost: 70,
        damage: 12,
        range: 120,
        fireRate: 5.5,
        damageType: 'BALLISTIC',
        critChance: 0.08,
        visual: 'mk2',
      },
      {
        name: 'MK III',
        cost: 120,
        damage: 16,
        range: 130,
        fireRate: 6.5,
        damageType: 'BALLISTIC',
        critChance: 0.12,
        projectiles: 2,
        visual: 'mk3',
      },
      {
        name: 'EXPERIMENTAL',
        cost: 220,
        damage: 18,
        range: 140,
        fireRate: 7.5,
        damageType: 'BALLISTIC',
        critChance: 0.18,
        projectiles: 2,
        armourPen: 0.25,
        specialName: 'ROTOR OVERDRIVE',
        specialDesc: 'Extreme fire-rate burst for 5s.',
        specialCooldown: 45,
        specialDamageMul: 1.4,
        specialDuration: 5,
        visual: 'exp',
      },
    ],
    evolvesTo: [
      { to: 'vulcan', name: 'CYCLONE GATLING', desc: 'Convert to a spin-up gatling with higher sustained rate.', cost: 120 },
      { to: 'sabre', name: 'SABRE BATTERY', desc: 'Convert to guided missile fire.', cost: 150 },
    ],
    symbiosis: ['pod_missile', 'pod_autocannon', 'pod_laser', 'pod_cluster', 'pod_uplink'],
  },
  thermal: {
    id: 'thermal',
    name: 'Pyre Lance',
    short: 'PYRE',
    desc: 'Short-range thermal projector. Burns swarms and leaves scorch.',
    colour: 0xff7a3c,
    targeting: 'ground',
    stages: [
      {
        name: 'BASE',
        cost: 55,
        damage: 6,
        range: 96,
        fireRate: 6,
        damageType: 'THERMAL',
        splash: 28,
        burnDps: 4,
        visual: 'mk1',
      },
      {
        name: 'MK II',
        cost: 85,
        damage: 9,
        range: 108,
        fireRate: 6.375,
        damageType: 'THERMAL',
        splash: 34,
        burnDps: 6,
        visual: 'mk2',
      },
      {
        name: 'MK III',
        cost: 140,
        damage: 12,
        range: 120,
        fireRate: 6.75,
        damageType: 'THERMAL',
        splash: 42,
        burnDps: 9,
        visual: 'mk3',
      },
      {
        name: 'EXPERIMENTAL',
        cost: 240,
        damage: 16,
        range: 135,
        fireRate: 7.5,
        damageType: 'THERMAL',
        splash: 55,
        burnDps: 14,
        specialName: 'THERMAL PURGE',
        specialDesc: 'Massive flame burst around the tower for 4s.',
        specialCooldown: 50,
        specialDamageMul: 2.2,
        specialDuration: 4,
        specialRange: 130,
        visual: 'exp',
      },
    ],
    evolvesTo: [
      { to: 'cryo', name: 'FLUX LANCE', desc: 'Convert to a cryo projector that freezes swarms.', cost: 110 },
    ],
    symbiosis: ['pod_cryo', 'pod_frost', 'pod_plasma', 'pod_missile', 'pod_shield'],
  },
  artillery: {
    id: 'artillery',
    name: 'Mordant Howitzer',
    short: 'MORDANT',
    desc: 'Long-range saturation fire. Weak up close, deadly against groups.',
    colour: 0xd4a24a,
    targeting: 'ground',
    stages: [
      {
        name: 'BASE',
        cost: 90,
        damage: 38,
        range: 220,
        fireRate: 0.55,
        damageType: 'EXPLOSIVE',
        splash: 48,
        visual: 'mk1',
      },
      {
        name: 'MK II',
        cost: 130,
        damage: 52,
        range: 240,
        fireRate: 0.6,
        damageType: 'EXPLOSIVE',
        splash: 56,
        visual: 'mk2',
      },
      {
        name: 'MK III',
        cost: 200,
        damage: 72,
        range: 260,
        fireRate: 0.65,
        damageType: 'EXPLOSIVE',
        splash: 68,
        armourPen: 0.15,
        visual: 'mk3',
      },
      {
        name: 'EXPERIMENTAL',
        cost: 320,
        damage: 95,
        range: 280,
        fireRate: 0.7,
        damageType: 'EXPLOSIVE',
        splash: 82,
        armourPen: 0.3,
        specialName: 'SATURATION BOMBARDMENT',
        specialDesc: 'Click map to rain shells on a zone.',
        specialCooldown: 70,
        specialDamageMul: 1.8,
        specialDuration: 3,
        specialRange: 200,
        visual: 'exp',
      },
    ],
    evolvesTo: [
      { to: 'sabre', name: 'SABRE BATTERY', desc: 'Convert to homing missiles and salvos.', cost: 130 },
    ],
    symbiosis: ['pod_missile', 'pod_autocannon', 'pod_cluster', 'pod_frost', 'pod_uplink'],
  },
  energy: {
    id: 'energy',
    name: 'Helios Arc',
    short: 'HELIOS',
    desc: 'High-output energy lances. Disrupts armour and shields.',
    colour: 0xb06cff,
    targeting: 'ground',
    stages: [
      {
        name: 'BASE',
        cost: 120,
        damage: 28,
        range: 140,
        fireRate: 1.2,
        damageType: 'ENERGY',
        armourPen: 0.2,
        visual: 'mk1',
      },
      {
        name: 'MK II',
        cost: 160,
        damage: 40,
        range: 155,
        fireRate: 1.3,
        damageType: 'ENERGY',
        armourPen: 0.3,
        visual: 'mk2',
      },
      {
        name: 'MK III',
        cost: 240,
        damage: 58,
        range: 170,
        fireRate: 1.4,
        damageType: 'ENERGY',
        armourPen: 0.4,
        slowPct: 0.15,
        visual: 'mk3',
      },
      {
        name: 'EXPERIMENTAL',
        cost: 380,
        damage: 85,
        range: 190,
        fireRate: 1.5,
        damageType: 'ENERGY',
        armourPen: 0.5,
        slowPct: 0.25,
        specialName: 'ENERGY OVERLOAD',
        specialDesc: 'Devastating charged lance shot (click target area).',
        specialCooldown: 55,
        specialDamageMul: 6,
        specialDuration: 1,
        specialRange: 280,
        visual: 'exp',
      },
    ],
    evolvesTo: [
      { to: 'tesla', name: 'ARC COIL', desc: 'Convert to chaining lightning arcs.', cost: 120 },
    ],
    symbiosis: ['pod_arc', 'pod_laser', 'pod_plasma', 'pod_uplink', 'pod_shield'],
  },
  vulcan: {
    id: 'vulcan',
    name: 'Cyclone Gatling',
    short: 'CYCLONE',
    desc: 'Spin-up rotary cannon. Rips apart light and medium targets at close range.',
    colour: 0x8fe36b,
    targeting: 'ground',
    stages: [
      {
        name: 'BASE',
        cost: 50,
        damage: 5,
        range: 116,
        fireRate: 6,
        damageType: 'BALLISTIC',
        visual: 'mk1',
      },
      {
        name: 'MK II',
        cost: 80,
        damage: 7,
        range: 128,
        fireRate: 8,
        damageType: 'BALLISTIC',
        critChance: 0.06,
        visual: 'mk2',
      },
      {
        name: 'MK III',
        cost: 140,
        damage: 9,
        range: 140,
        fireRate: 11,
        damageType: 'BALLISTIC',
        critChance: 0.1,
        projectiles: 2,
        visual: 'mk3',
      },
      {
        name: 'EXPERIMENTAL',
        cost: 240,
        damage: 11,
        range: 152,
        fireRate: 14,
        damageType: 'BALLISTIC',
        critChance: 0.14,
        projectiles: 3,
        armourPen: 0.2,
        specialName: 'CYCLONE SPIN-UP',
        specialDesc: 'Overdrive the rotary to maximum rate for 5s.',
        specialCooldown: 42,
        specialDamageMul: 1.5,
        specialDuration: 5,
        visual: 'exp',
      },
    ],
    evolvesTo: [
      { to: 'sabre', name: 'SABRE BATTERY', desc: 'Convert the rotary into guided missile fire.', cost: 140 },
      { to: 'ballistic', name: 'KESTREL REFIT', desc: 'Revert to a precision autocannon.', cost: 80 },
    ],
    symbiosis: ['pod_missile', 'pod_salvo', 'pod_autocannon', 'pod_laser', 'pod_cluster', 'pod_uplink'],
  },
  sabre: {
    id: 'sabre',
    name: 'Sabre Missile Battery',
    short: 'SABRE',
    desc: 'Guided missile battery. Locks on and delivers explosive salvos at long range.',
    colour: 0xffb347,
    targeting: 'ground',
    stages: [
      {
        name: 'BASE',
        cost: 110,
        damage: 46,
        range: 200,
        fireRate: 0.7,
        damageType: 'EXPLOSIVE',
        splash: 55,
        homing: true,
        visual: 'mk1',
      },
      {
        name: 'MK II',
        cost: 150,
        damage: 62,
        range: 220,
        fireRate: 0.8,
        damageType: 'EXPLOSIVE',
        splash: 62,
        homing: true,
        visual: 'mk2',
      },
      {
        name: 'MK III',
        cost: 220,
        damage: 82,
        range: 240,
        fireRate: 0.9,
        damageType: 'EXPLOSIVE',
        splash: 72,
        armourPen: 0.12,
        projectiles: 2,
        homing: true,
        visual: 'mk3',
      },
      {
        name: 'EXPERIMENTAL',
        cost: 340,
        damage: 105,
        range: 260,
        fireRate: 1,
        damageType: 'EXPLOSIVE',
        splash: 88,
        armourPen: 0.25,
        projectiles: 3,
        homing: true,
        specialName: 'MISSILE SALVO',
        specialDesc: 'Fire a full rack of missiles at the target zone.',
        specialCooldown: 60,
        specialDamageMul: 1.8,
        specialDuration: 3,
        specialRange: 240,
        visual: 'exp',
      },
    ],
    evolvesTo: [
      { to: 'vulcan', name: 'CYCLONE GATLING', desc: 'Strip the launcher for a rotary cannon.', cost: 140 },
      { to: 'artillery', name: 'MORDANT REFIT', desc: 'Revert to an unguided howitzer.', cost: 90 },
    ],
    symbiosis: ['pod_autocannon', 'pod_missile', 'pod_cluster', 'pod_plasma', 'pod_uplink'],
  },
  tesla: {
    id: 'tesla',
    name: 'Tesla Arc Coil',
    short: 'TESLA',
    desc: 'Arc projector that chains lightning between clustered hostiles.',
    colour: 0x66e0ff,
    targeting: 'ground',
    stages: [
      {
        name: 'BASE',
        cost: 95,
        damage: 22,
        range: 130,
        fireRate: 1.6,
        damageType: 'ENERGY',
        armourPen: 0.15,
        chain: 2,
        visual: 'mk1',
      },
      {
        name: 'MK II',
        cost: 140,
        damage: 30,
        range: 145,
        fireRate: 1.8,
        damageType: 'ENERGY',
        armourPen: 0.22,
        chain: 3,
        visual: 'mk2',
      },
      {
        name: 'MK III',
        cost: 210,
        damage: 40,
        range: 160,
        fireRate: 2,
        damageType: 'ENERGY',
        armourPen: 0.32,
        slowPct: 0.12,
        chain: 4,
        visual: 'mk3',
      },
      {
        name: 'EXPERIMENTAL',
        cost: 330,
        damage: 55,
        range: 175,
        fireRate: 2.2,
        damageType: 'ENERGY',
        armourPen: 0.42,
        slowPct: 0.2,
        chain: 6,
        specialName: 'OVERLOAD STORM',
        specialDesc: 'Unleash a chaining storm that arcs across the whole cluster.',
        specialCooldown: 52,
        specialDamageMul: 2.6,
        specialDuration: 3,
        specialRange: 200,
        visual: 'exp',
      },
    ],
    evolvesTo: [
      { to: 'energy', name: 'HELIOS REFIT', desc: 'Revert to a single-target lance.', cost: 100 },
      { to: 'cryo', name: 'FLUX LANCE', desc: 'Convert to a freezing cryo projector.', cost: 120 },
    ],
    symbiosis: ['pod_arc', 'pod_laser', 'pod_cryo', 'pod_uplink'],
  },
  cryo: {
    id: 'cryo',
    name: 'Cryo Flux Lance',
    short: 'CRYO',
    desc: 'Cryogenic projector. Low damage, heavy slowing — locks swarms in place.',
    colour: 0x9fd8ff,
    targeting: 'ground',
    stages: [
      {
        name: 'BASE',
        cost: 70,
        damage: 4,
        range: 112,
        fireRate: 5,
        damageType: 'THERMAL',
        splash: 30,
        slowPct: 0.35,
        visual: 'mk1',
      },
      {
        name: 'MK II',
        cost: 100,
        damage: 6,
        range: 124,
        fireRate: 6,
        damageType: 'THERMAL',
        splash: 40,
        slowPct: 0.45,
        visual: 'mk2',
      },
      {
        name: 'MK III',
        cost: 160,
        damage: 8,
        range: 136,
        fireRate: 7,
        damageType: 'THERMAL',
        splash: 50,
        slowPct: 0.55,
        visual: 'mk3',
      },
      {
        name: 'EXPERIMENTAL',
        cost: 260,
        damage: 11,
        range: 150,
        fireRate: 8,
        damageType: 'THERMAL',
        splash: 62,
        slowPct: 0.65,
        specialName: 'FLASH FREEZE',
        specialDesc: 'Detonate a cryo field that near-freezes everything nearby.',
        specialCooldown: 48,
        specialDamageMul: 2,
        specialDuration: 4,
        specialRange: 140,
        visual: 'exp',
      },
    ],
    evolvesTo: [
      { to: 'thermal', name: 'PYRE REFIT', desc: 'Revert to an incendiary projector.', cost: 80 },
      { to: 'tesla', name: 'ARC COIL', desc: 'Convert to a chaining arc projector.', cost: 120 },
    ],
    symbiosis: ['pod_cryo', 'pod_frost', 'pod_plasma', 'pod_missile', 'pod_shield'],
  },
  dronehive: {
    id: 'dronehive',
    name: 'Wasp Drone Hive',
    short: 'SWARM',
    desc: 'Launches independent strike drones that sortie to close targets, then return to rearm.',
    colour: 0x5ec8ff,
    targeting: 'any',
    stages: [
      {
        name: 'BASE',
        cost: 115,
        damage: 16,
        range: 150,
        fireRate: 0.7,
        damageType: 'BALLISTIC',
        splash: 26,
        visual: 'mk1',
        specialName: 'SWARM SORTIE',
        specialDesc: 'Launches the full drone wing for a coordinated strike.',
        specialCooldown: 36,
        specialDuration: 4,
      },
      {
        name: 'MK II',
        cost: 145,
        damage: 21,
        range: 164,
        fireRate: 0.8,
        damageType: 'BALLISTIC',
        splash: 30,
        visual: 'mk2',
        specialName: 'SWARM SORTIE',
        specialDesc: 'Launches the full drone wing for a coordinated strike.',
        specialCooldown: 34,
        specialDuration: 4.5,
      },
      {
        name: 'MK III',
        cost: 220,
        damage: 28,
        range: 178,
        fireRate: 0.9,
        damageType: 'BALLISTIC',
        splash: 34,
        visual: 'mk3',
        specialName: 'SWARM SORTIE',
        specialDesc: 'Launches the full drone wing for a coordinated strike.',
        specialCooldown: 32,
        specialDuration: 5,
      },
      {
        name: 'EXPERIMENTAL',
        cost: 340,
        damage: 36,
        range: 194,
        fireRate: 1,
        damageType: 'BALLISTIC',
        splash: 40,
        visual: 'exp',
        specialName: 'APOGEE SWARM',
        specialDesc: 'Six drones fan out with machine guns, bombs and micro-missiles.',
        specialCooldown: 28,
        specialDuration: 6,
      },
    ],
    symbiosis: ['pod_autocannon', 'pod_missile', 'pod_cluster', 'pod_uplink'],
  },
};

export const TOWER_ORDER = ['ballistic', 'thermal', 'artillery', 'energy', 'vulcan', 'sabre', 'tesla', 'cryo', 'dronehive'] as const;
